import { Mutable } from '@breezy/shared'
import { useEffect, useState } from 'react'

interface ScriptProps {
  src: HTMLScriptElement['src'] | null
  checkForExisting?: boolean
}

export type ErrorState = ErrorEvent | null
type ScriptStatus = {
  loading: boolean
  error: ErrorState
  scriptEl: HTMLScriptElement
}
type ScriptStatusMap = {
  [key: string]: ScriptStatus
}

// Previously loading/loaded scripts and their current status
const scripts: ScriptStatusMap = {}

// Check for existing <script> tags with this src. If so, update scripts[src]
// and return the new status; otherwise, return undefined.
const checkExisting = (src: string): ScriptStatus | undefined => {
  const existing: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`)
  if (existing) {
    // Assume existing <script> tag is already loaded,
    // and cache that data for future use.
    return (scripts[src] = {
      loading: false,
      error: null,
      scriptEl: existing,
    })
  }
  return undefined
}

export default function useScript({ src, checkForExisting = false }: ScriptProps): [boolean, ErrorState] {
  // Check whether some instance of this hook considered this src.
  let status: ScriptStatus | undefined = src ? scripts[src] : undefined

  // If requested, check for existing <script> tags with this src
  // (unless we've already loaded the script ourselves).
  if (!status && checkForExisting && src && isBrowser) {
    status = checkExisting(src)
  }

  const [loading, setLoading] = useState<boolean>(status ? status.loading : Boolean(src))
  const [error, setError] = useState<ErrorState>(status ? status.error : null)
  // Tracks if script is loaded so we can avoid duplicate script tags
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Complex script loading logic with intentionally limited dependencies
  useEffect(() => {
    // Nothing to do on server, or if no src specified, or
    // if script is already loaded or "error" state.
    if (!isBrowser || !src || scriptLoaded || error) return

    // Check again for existing <script> tags with this src
    // in case it's changed since mount.
    status = scripts[src]
    if (!status && checkForExisting) {
      status = checkExisting(src)
    }

    // Determine or create <script> element to listen to.
    let scriptEl: Mutable<HTMLScriptElement>
    if (status) {
      scriptEl = status.scriptEl
    } else {
      scriptEl = document.createElement('script')
      scriptEl.src = src

      status = scripts[src] = {
        loading: true,
        error: null,
        scriptEl: scriptEl,
      }
    }
    // `status` is now guaranteed to be defined: either the old status
    // from a previous load, or a newly created one.

    const handleLoad = () => {
      if (status) status.loading = false
      setLoading(false)
      setScriptLoaded(true)
    }
    const handleError = (error: ErrorEvent) => {
      if (status) status.error = error
      setError(error)
    }

    scriptEl.addEventListener('load', handleLoad)
    scriptEl.addEventListener('error', handleError)

    document.body.appendChild(scriptEl)

    return () => {
      scriptEl.removeEventListener('load', handleLoad)
      scriptEl.removeEventListener('error', handleError)
    }
    // we need to ignore the attributes as they're a new object per call, so we'd never skip an effect call
  }, [src])

  return [loading, error]
}

const isBrowser =
  // eslint-disable-next-line breezy/no-typeof-null-or-undefined
  typeof window !== 'undefined' && typeof window.document !== 'undefined'

// Source Code Respository: https://github.com/hupe1980/react-script-hook
// Note: Used from Source instead of NPM package due to Webpack 5 Source Maps Issue that's currently unsolved in the repo as of 12/21/22
// Open Github Issue: https://github.com/hupe1980/react-script-hook/issues/29

// MIT License

// Copyright (c) 2019

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
